import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Loader2, Check, X } from 'lucide-react';
import { API } from '../api';

export default function PaymentResult() {
  const nav = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();
  const cancelled = loc.pathname.endsWith('/cancel');
  const [state, setState] = useState(cancelled ? 'cancelled' : 'checking');
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (cancelled) return;
    const sid = params.get('session_id');
    if (!sid) { setState('error'); return; }
    let tries = 0, stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const s = await API.paymentStatus(sid);
        if (s.payment_status === 'paid') { setInfo(s); setState('paid'); return; }
        if (['expired', 'failed'].includes(s.payment_status)) { setState('error'); return; }
      } catch (e) { if (e?.response?.status === 404) { setState('error'); return; } }
      if (++tries < 10) setTimeout(poll, 2000); else setState('timeout');
    };
    poll();
    return () => { stop = true; };
  }, []); // eslint-disable-line

  const copy = {
    checking: ['Confirming your payment', 'One moment while Stripe finishes up.', null],
    paid: ['Payment confirmed.', { song: 'Your song is unlocked. Your full track and receipt are waiting in your dashboard.', launchpad: 'Your Social Media Launch Pad is unlocked. Make your videos and grab your captions in your dashboard.', subscription: 'Your Social Media Management plan is active. Perry will reach out within one business day to start your setup.' }[info?.kind] || 'Perry has your go-ahead. Your receipt is in your dashboard.', 'Go to my dashboard'],
    cancelled: ['Checkout cancelled.', 'Nothing was charged. Your song is still waiting whenever you\u2019re ready.', 'Back to my dashboard'],
    error: ['Something went wrong.', 'We couldn\u2019t confirm that payment. If you were charged, email info@brandmelody.com and we\u2019ll sort it out.', 'Back to my dashboard'],
    timeout: ['Still processing.', 'Your payment is taking a moment. Check your dashboard in a minute \u2014 it will update automatically.', 'Go to my dashboard'],
  }[state];

  return (
    <div className="min-h-screen bg-ink grain grid place-items-center px-6">
      <div className="panel rounded-3xl p-10 max-w-lg w-full text-center" data-testid="payment-result">
        <span className={`grid place-items-center w-16 h-16 rounded-full mx-auto mb-6 ${state === 'paid' ? 'bg-gold text-ink' : state === 'checking' ? 'bg-gold/10' : 'border border-gold/40 text-gold'}`}>
          {state === 'checking' ? <Loader2 className="text-gold animate-spin" size={26} /> : state === 'paid' ? <Check size={28} /> : <X size={26} />}
        </span>
        <h1 className="font-display text-4xl text-cream">{copy[0]}</h1>
        <p className="mt-3 text-cream/60">{copy[1]}</p>
        {copy[2] && <button data-testid="payment-result-cta" onClick={() => nav(['song', 'launchpad'].includes(info?.kind) && info?.code ? `/dashboard?song=${info.code}` : '/dashboard')} className="btn-gold rounded-full px-7 py-3.5 mt-8 inline-flex items-center gap-2">{copy[2]}</button>}
      </div>
    </div>
  );
}
